import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Role, RolePermission, ModuleDefinition } from '../../../../../types/cms.types';
import './CpnRoleForm.css';

interface CpnRoleFormProps {
    role?: Role | null;
    onSubmit: (data: any) => void;
    onCancel: () => void;
    loading?: boolean;
    availableModules: ModuleDefinition[];
    availableTemplates: any[];
    onCreateFromTemplate?: (templateName: string) => void;
}

export const CpnRoleForm: React.FC<CpnRoleFormProps> = ({
    role,
    onSubmit,
    onCancel,
    loading = false,
    availableModules,
    availableTemplates,
    onCreateFromTemplate
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: [] as RolePermission[]
    });

    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showTemplates, setShowTemplates] = useState(!role); // Show templates only for new roles

    // Initialize form data when role prop changes
    useEffect(() => {
        if (role) {
            setFormData({
                name: role.name || '',
                description: role.description || '',
                permissions: role.permissions || []
            });
            setShowTemplates(false);
        } else {
            setFormData({
                name: '',
                description: '',
                permissions: []
            });
            setShowTemplates(true);
        }
        setErrors({});
    }, [role]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handlePermissionChange = (
        moduleIndex: number,
        subModuleIndex: number | null,
        permissionType: string,
        value: boolean | undefined
    ) => {
        if (value === undefined) return;

        const newPermissions = [...formData.permissions];

        if (subModuleIndex !== null) {
            // Sub module permission
            const module = availableModules[moduleIndex];
            const subModule = module.subModules![subModuleIndex];

            let existingPermission = newPermissions.find(p =>
                p.module_name === module.name && p.sub_module_name === subModule.name
            );

            if (!existingPermission) {
                existingPermission = {
                    module_name: module.name,
                    sub_module_name: subModule.name,
                    view_permission: false,
                    add_permission: false,
                    edit_permission: false,
                    delete_permission: false
                };
                newPermissions.push(existingPermission);
            }

            (existingPermission as any)[permissionType] = value;
        } else {
            // Main module permission
            const module = availableModules[moduleIndex];

            let existingPermission = newPermissions.find(p =>
                p.module_name === module.name && !p.sub_module_name
            );

            if (!existingPermission) {
                existingPermission = {
                    module_name: module.name,
                    view_permission: false,
                    add_permission: false,
                    edit_permission: false,
                    delete_permission: false
                };
                newPermissions.push(existingPermission);
            }

            (existingPermission as any)[permissionType] = value;
        }

        setFormData(prev => ({ ...prev, permissions: newPermissions }));
    };

    const getPermissionValue = (
        moduleName: string,
        subModuleName: string | undefined,
        permissionType: string
    ): boolean => {
        const permission = formData.permissions.find(p =>
            p.module_name === moduleName && p.sub_module_name === subModuleName
        );
        return permission ? (permission as any)[permissionType] : false;
    };

    const handleTemplateSelect = async (templateName: string) => {
        if (templateName && onCreateFromTemplate) {
            try {
                await onCreateFromTemplate(templateName);
                onCancel(); // Close dialog after success
            } catch (error) {
                console.error('Error creating role from template:', error);
            }
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Vui lòng nhập tên vai trò';
        }

        // At least one permission must be selected
        const hasPermission = formData.permissions.some(p =>
            p.view_permission || p.add_permission || p.edit_permission || p.delete_permission
        );

        if (!hasPermission) {
            newErrors.permissions = 'Vui lòng chọn ít nhất một quyền';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Filter out permissions that have no permissions set
        const validPermissions = formData.permissions.filter(p =>
            p.view_permission || p.add_permission || p.edit_permission || p.delete_permission
        );

        const submitData = {
            ...formData,
            permissions: validPermissions
        };

        onSubmit(submitData);
    };

    const templateOptions = [
        { label: 'Chọn mẫu vai trò...', value: '' },
        ...availableTemplates.map(template => ({
            label: `${template.name} - ${template.description}`,
            value: template.name
        }))
    ];

    return (
        <form onSubmit={handleSubmit} className="role-form">
            <div className="grid">
                {/* Left Column - Basic Info */}
                <div className="col-12 lg:col-3">
                    {/* Template Selection for New Roles */}
                    {showTemplates && !role && (
                        <Card className="mb-4">
                            <h5>Tạo từ mẫu có sẵn</h5>
                            <div className="flex flex-column gap-2">
                                <Dropdown
                                    value={selectedTemplate}
                                    options={templateOptions}
                                    onChange={(e) => setSelectedTemplate(e.value)}
                                    placeholder="Chọn mẫu vai trò"
                                    className="w-full"
                                />
                                <Button
                                    label="Tạo từ mẫu"
                                    icon="pi pi-plus"
                                    onClick={() => handleTemplateSelect(selectedTemplate)}
                                    disabled={!selectedTemplate || loading}
                                    type="button"
                                    className="w-full"
                                />
                            </div>
                            <Divider align="center">
                                <span className="text-sm text-500">HOẶC</span>
                            </Divider>
                        </Card>
                    )}

                    {/* Role Name */}
                    <div className="field mb-4">
                        <label className="block text-900 font-medium mb-2">
                            Tên vai trò <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Nhập tên vai trò"
                            className={`w-full ${errors.name ? 'p-invalid' : ''}`}
                        />
                        {errors.name && (
                            <small className="p-error">{errors.name}</small>
                        )}
                    </div>

                    {/* Description */}
                    <div className="field mb-4">
                        <label className="block text-900 font-medium mb-2">
                            Mô tả
                        </label>
                        <InputTextarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Nhập mô tả vai trò"
                            className="w-full"
                            rows={5}
                            autoResize
                        />
                    </div>

                    {/* Form Actions - Moved to left column */}
                    <div className="form-actions-left">
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            onClick={onCancel}
                            type="button"
                            disabled={loading}
                            className="w-full mb-2"
                        />
                        <Button
                            label={role ? 'Cập nhật' : 'Tạo mới'}
                            icon={role ? 'pi pi-check' : 'pi pi-plus'}
                            type="submit"
                            loading={loading}
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Right Column - Permissions */}
                <div className="col-12 lg:col-9">
                    <div className="field">
                        <label className="block text-900 font-medium mb-3">
                            Quyền hạn <span className="text-red-500">*</span>
                        </label>

                        <div className="permissions-container">
                            {availableModules.map((module, moduleIndex) => (
                                <Panel
                                    key={module.name}
                                    header={module.displayName}
                                    className="mb-3"
                                    toggleable
                                    collapsed={false}
                                >
                                    {/* Main Module Permissions */}

                                    {
                                        !module.subModules && (
                                            <div className="permission-row mb-3 p-3 bg-gray-50 border-round">
                                                <div className="flex align-items-center justify-content-between">
                                                    <span className="font-medium">{module.displayName}</span>
                                                    <div className="flex gap-4">
                                                        <div className="field-checkbox">
                                                            <Checkbox
                                                                inputId={`${module.name}_view`}
                                                                checked={getPermissionValue(module.name, undefined, 'view_permission')}
                                                                onChange={(e) => handlePermissionChange(moduleIndex, null, 'view_permission', e.checked)}
                                                            />
                                                            <label htmlFor={`${module.name}_view`} className="ml-1">Xem</label>
                                                        </div>
                                                        <div className="field-checkbox">
                                                            <Checkbox
                                                                inputId={`${module.name}_add`}
                                                                checked={getPermissionValue(module.name, undefined, 'add_permission')}
                                                                onChange={(e) => handlePermissionChange(moduleIndex, null, 'add_permission', e.checked)}
                                                            />
                                                            <label htmlFor={`${module.name}_add`} className="ml-1">Thêm</label>
                                                        </div>
                                                        <div className="field-checkbox">
                                                            <Checkbox
                                                                inputId={`${module.name}_edit`}
                                                                checked={getPermissionValue(module.name, undefined, 'edit_permission')}
                                                                onChange={(e) => handlePermissionChange(moduleIndex, null, 'edit_permission', e.checked)}
                                                            />
                                                            <label htmlFor={`${module.name}_edit`} className="ml-1">Sửa</label>
                                                        </div>
                                                        <div className="field-checkbox">
                                                            <Checkbox
                                                                inputId={`${module.name}_delete`}
                                                                checked={getPermissionValue(module.name, undefined, 'delete_permission')}
                                                                onChange={(e) => handlePermissionChange(moduleIndex, null, 'delete_permission', e.checked)}
                                                            />
                                                            <label htmlFor={`${module.name}_delete`} className="ml-1">Xóa</label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }



                                    {/* Sub Module Permissions */}
                                    {module.subModules && module.subModules.map((subModule, subModuleIndex) => (
                                        <div key={subModule.name} className="permission-row mb-2 p-2 pl-4 border-left-3 border-primary-200">
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm">{subModule.displayName}</span>
                                                <div className="flex gap-4">
                                                    <div className="field-checkbox">
                                                        <Checkbox
                                                            inputId={`${module.name}_${subModule.name}_view`}
                                                            checked={getPermissionValue(module.name, subModule.name, 'view_permission')}
                                                            onChange={(e) => handlePermissionChange(moduleIndex, subModuleIndex, 'view_permission', e.checked)}
                                                        />
                                                        <label htmlFor={`${module.name}_${subModule.name}_view`} className="ml-1 text-sm">Xem</label>
                                                    </div>
                                                    <div className="field-checkbox">
                                                        <Checkbox
                                                            inputId={`${module.name}_${subModule.name}_add`}
                                                            checked={getPermissionValue(module.name, subModule.name, 'add_permission')}
                                                            onChange={(e) => handlePermissionChange(moduleIndex, subModuleIndex, 'add_permission', e.checked)}
                                                        />
                                                        <label htmlFor={`${module.name}_${subModule.name}_add`} className="ml-1 text-sm">Thêm</label>
                                                    </div>
                                                    <div className="field-checkbox">
                                                        <Checkbox
                                                            inputId={`${module.name}_${subModule.name}_edit`}
                                                            checked={getPermissionValue(module.name, subModule.name, 'edit_permission')}
                                                            onChange={(e) => handlePermissionChange(moduleIndex, subModuleIndex, 'edit_permission', e.checked)}
                                                        />
                                                        <label htmlFor={`${module.name}_${subModule.name}_edit`} className="ml-1 text-sm">Sửa</label>
                                                    </div>
                                                    <div className="field-checkbox">
                                                        <Checkbox
                                                            inputId={`${module.name}_${subModule.name}_delete`}
                                                            checked={getPermissionValue(module.name, subModule.name, 'delete_permission')}
                                                            onChange={(e) => handlePermissionChange(moduleIndex, subModuleIndex, 'delete_permission', e.checked)}
                                                        />
                                                        <label htmlFor={`${module.name}_${subModule.name}_delete`} className="ml-1 text-sm">Xóa</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </Panel>
                            ))}
                        </div>

                        {errors.permissions && (
                            <small className="p-error">{errors.permissions}</small>
                        )}
                    </div>
                </div>
            </div>
        </form>
    );
};
